import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { DashboardLayout } from '../../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Plus, Pencil, Trash2, Search, Upload, Download, AlertTriangle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function V2Questions() {
  const { token } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');

  // Dialog states
  const [editOpen, setEditOpen] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [saving, setSaving] = useState(false);

  // CSV import
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvRef = useRef(null);

  // Duplicate scan
  const [dupResult, setDupResult] = useState(null);
  const [dupOpen, setDupOpen] = useState(false);
  const [scanning, setScanning] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchQuestions = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterCategory !== 'all') params.category_id = filterCategory;
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/v2/questions`, { headers, params });
      setQuestions(res.data);
    } catch (err) {
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v2/categories`, { headers, params: { status: 'active' } });
      setCategories(res.data);
    } catch (err) {}
  };

  useEffect(() => { fetchQuestions(); fetchCategories(); }, [filterCategory, filterStatus, search]);

  const blankQuestion = { question_text: '', category_id: '', university_id: '', keywords: [], expected_points: [], answer_relevancy_guidance: '', weight: 1, difficulty: 'medium', status: 'active' };

  const openNew = () => { setEditQuestion({ ...blankQuestion }); setEditOpen(true); };
  const openEdit = (q) => { setEditQuestion({ ...q }); setEditOpen(true); };

  const handleSave = async () => {
    if (!editQuestion.question_text?.trim()) { toast.error('Question text is required'); return; }
    setSaving(true);
    try {
      if (editQuestion.id) {
        await axios.put(`${API_URL}/api/v2/questions/${editQuestion.id}`, editQuestion, { headers });
        toast.success('Question updated');
      } else {
        await axios.post(`${API_URL}/api/v2/questions`, editQuestion, { headers });
        toast.success('Question created');
      }
      setEditOpen(false);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/v2/questions/${id}`, { headers });
      toast.success('Question archived');
      fetchQuestions();
    } catch (err) {
      toast.error('Failed to archive');
    }
  };

  // CSV
  const handleCSVFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { toast.error('CSV needs header + data'); return; }
      const parseCSVLine = (line) => {
        const res = []; let cur = ''; let inQ = false;
        for (let i = 0; i < line.length; i++) {
          if (line[i] === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
          else if (line[i] === ',' && !inQ) { res.push(cur.trim()); cur = ''; }
          else cur += line[i];
        }
        res.push(cur.trim());
        return res;
      };
      const hdrs = parseCSVLine(lines[0]);
      const rows = lines.slice(1).map(l => {
        const vals = parseCSVLine(l);
        const obj = {};
        hdrs.forEach((h, i) => { obj[h.trim()] = vals[i] || ''; });
        return obj;
      }).filter(r => (r['Question'] || r['question_text'] || '').trim());
      setCsvPreview(rows);
      setCsvOpen(true);
    };
    reader.readAsText(file);
    if (csvRef.current) csvRef.current.value = '';
  };

  const handleCSVImport = async () => {
    setCsvImporting(true);
    try {
      const res = await axios.post(`${API_URL}/api/v2/questions/import-csv`, { questions: csvPreview }, { headers });
      toast.success(`Imported ${res.data.imported}, skipped ${res.data.skipped}`);
      if (res.data.errors?.length) res.data.errors.slice(0, 5).forEach(e => toast.warning(e));
      setCsvOpen(false); setCsvPreview([]);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCsvImporting(false);
    }
  };

  const handleDupScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API_URL}/api/v2/questions/detect-duplicates`, {}, { headers });
      setDupResult(res.data);
      setDupOpen(true);
    } catch (err) {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const catName = (catId) => categories.find(c => c.id === catId)?.name || 'Uncategorized';

  return (
    <DashboardLayout role="super_admin">
      <div className="space-y-4" data-testid="v2-questions">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold">V2 Question Bank</h1>
          <div className="flex gap-2 flex-wrap">
            <input type="file" ref={csvRef} accept=".csv" onChange={handleCSVFile} className="hidden" />
            <Button variant="outline" size="sm" onClick={() => csvRef.current?.click()} data-testid="v2-import-csv">
              <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.open(`${API_URL}/api/v2/questions/export-csv?token=${token}`, '_blank')} data-testid="v2-export-csv">
              <Download className="w-4 h-4 mr-1.5" /> Export
            </Button>
            <Button variant="outline" size="sm" onClick={handleDupScan} disabled={scanning} data-testid="v2-scan-dupes">
              <Copy className="w-4 h-4 mr-1.5" /> {scanning ? 'Scanning...' : 'Scan Duplicates'}
            </Button>
            <Button onClick={openNew} data-testid="v2-add-question">
              <Plus className="w-4 h-4 mr-1.5" /> Add Question
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search questions..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="v2-search" />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="px-3 py-2 rounded-md border bg-background text-sm">
            <option value="all">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2 rounded-md border bg-background text-sm">
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="all">All</option>
          </select>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-muted/50">
                  <th className="p-3 text-left font-medium w-[40%]">Question</th>
                  <th className="p-3 text-left font-medium">Category</th>
                  <th className="p-3 text-left font-medium">Difficulty</th>
                  <th className="p-3 text-left font-medium">Weight</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  <th className="p-3 text-right font-medium">Actions</th>
                </tr></thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading...</td></tr>
                  ) : questions.length === 0 ? (
                    <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No questions found. Add your first V2 question.</td></tr>
                  ) : questions.map(q => (
                    <tr key={q.id} className="border-b hover:bg-muted/30">
                      <td className="p-3"><p className="line-clamp-2">{q.question_text}</p></td>
                      <td className="p-3"><Badge variant="outline" className="text-xs">{q.category_name || catName(q.category_id)}</Badge></td>
                      <td className="p-3 capitalize">{q.difficulty || 'medium'}</td>
                      <td className="p-3">{q.weight}</td>
                      <td className="p-3"><Badge variant={q.status === 'active' ? 'default' : 'secondary'}>{q.status}</Badge></td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(q)} data-testid={`v2-edit-${q.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleArchive(q.id)} data-testid={`v2-archive-${q.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-3 border-t text-sm text-muted-foreground">{questions.length} question(s)</div>
          </CardContent>
        </Card>

        {/* Edit/Create Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editQuestion?.id ? 'Edit Question' : 'New Question'}</DialogTitle></DialogHeader>
            {editQuestion && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Question Text *</label>
                  <textarea className="w-full mt-1 p-2 border rounded-md bg-background min-h-[80px] text-sm" value={editQuestion.question_text} onChange={e => setEditQuestion({...editQuestion, question_text: e.target.value})} data-testid="v2-q-text" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <select className="w-full mt-1 p-2 border rounded-md bg-background text-sm" value={editQuestion.category_id || ''} onChange={e => setEditQuestion({...editQuestion, category_id: e.target.value || null})}>
                      <option value="">Uncategorized</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Difficulty</label>
                    <select className="w-full mt-1 p-2 border rounded-md bg-background text-sm" value={editQuestion.difficulty || 'medium'} onChange={e => setEditQuestion({...editQuestion, difficulty: e.target.value})}>
                      <option value="easy">Easy</option>
                      <option value="medium">Medium</option>
                      <option value="hard">Hard</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Answer Relevancy Guidance</label>
                  <textarea className="w-full mt-1 p-2 border rounded-md bg-background min-h-[60px] text-sm" value={editQuestion.answer_relevancy_guidance || ''} onChange={e => setEditQuestion({...editQuestion, answer_relevancy_guidance: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Keywords (comma-separated)</label>
                    <Input value={(editQuestion.keywords || []).join(', ')} onChange={e => setEditQuestion({...editQuestion, keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean)})} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Weight</label>
                    <Input type="number" min={1} value={editQuestion.weight || 1} onChange={e => setEditQuestion({...editQuestion, weight: parseInt(e.target.value) || 1})} />
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="v2-save-question">{saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CSV Import Dialog */}
        <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Import Questions from CSV</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground">{csvPreview.length} question(s) found</p>
            {csvPreview.length > 0 && (
              <div className="border rounded-lg overflow-auto max-h-[300px]">
                <table className="w-full text-sm">
                  <thead className="bg-muted sticky top-0"><tr>
                    <th className="p-2 text-left">#</th><th className="p-2 text-left">Question</th><th className="p-2 text-left">Category</th>
                  </tr></thead>
                  <tbody>{csvPreview.slice(0, 30).map((r, i) => (
                    <tr key={i} className="border-t"><td className="p-2">{i+1}</td><td className="p-2 max-w-[300px] truncate">{r.Question || r.question_text || ''}</td><td className="p-2">{r.Category || r.category || ''}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCsvOpen(false)}>Cancel</Button>
              <Button onClick={handleCSVImport} disabled={csvImporting} data-testid="v2-confirm-csv">{csvImporting ? 'Importing...' : `Import ${csvPreview.length} Questions`}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Duplicate Scan Dialog */}
        <Dialog open={dupOpen} onOpenChange={setDupOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Duplicate Scan Results</DialogTitle></DialogHeader>
            {dupResult && (
              <div className="space-y-4">
                <p className="text-sm">Scanned {dupResult.total_questions} questions</p>
                {dupResult.exact_duplicate_count > 0 ? (
                  <div>
                    <h3 className="font-medium text-red-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {dupResult.exact_duplicate_count} Exact Duplicate(s)</h3>
                    {dupResult.exact_duplicates.map((d, i) => (
                      <div key={i} className="mt-2 p-3 bg-red-50 dark:bg-red-950/20 rounded border border-red-200 dark:border-red-800 text-sm">
                        <p className="font-medium">"{d.texts[0]}" (x{d.count})</p>
                        <p className="text-xs text-muted-foreground mt-1">IDs: {d.question_ids.join(', ')}</p>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-green-600 text-sm">No exact duplicates found.</p>}
                {dupResult.near_duplicate_count > 0 && (
                  <div>
                    <h3 className="font-medium text-amber-600">{dupResult.near_duplicate_count} Near-Duplicate(s)</h3>
                    {dupResult.near_duplicates.slice(0, 10).map((d, i) => (
                      <div key={i} className="mt-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded border text-sm">
                        <p>{d.similarity}% similar</p>
                        <p className="text-xs mt-1">1: {d.question_1.text}</p>
                        <p className="text-xs">2: {d.question_2.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <DialogFooter><Button variant="outline" onClick={() => setDupOpen(false)}>Close</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
